import { useNavigate } from 'react-router';
import { ROUTES } from '@/constants';
import { toast } from '@/components/ui/use-toast';
import { useChangeParentEmailMutation, useRequestConsentMutation } from '@/store/api';
import { emailRegex } from './signup.constants';

interface UseSignupParentConsentParams {
	isRegisteredUser: boolean;
	isChangingParentEmail: boolean;
	studentEmail?: string;
	currentParentEmail?: string;
	setIsSignUpLoading: (value: boolean) => void;
	setIsParentConsentSubmitting: (value: boolean) => void;
}

const getApiErrorMessage = (error: unknown) =>
	(error as { data?: { message?: string } })?.data?.message;

export function useSignupParentConsent({
	isRegisteredUser,
	isChangingParentEmail,
	studentEmail,
	currentParentEmail,
	setIsSignUpLoading,
	setIsParentConsentSubmitting,
}: UseSignupParentConsentParams) {
	const navigate = useNavigate();
	const [requestConsent] = useRequestConsentMutation();
	const [changeParentEmail] = useChangeParentEmailMutation();

	const validateParentEmail = (value: string) => {
		const trimmedParentEmail = value.trim();

		if (!trimmedParentEmail) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Please enter a parent or guardian email address',
			});
			return null;
		}

		if (!emailRegex.test(trimmedParentEmail)) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Please enter a valid parent or guardian email address',
			});
			return null;
		}

		const normalizedParentEmail = trimmedParentEmail.toLowerCase();
		if (studentEmail?.trim().toLowerCase() === normalizedParentEmail) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: "Parent email must differ from the student's own email",
			});
			return null;
		}

		if (
			isChangingParentEmail &&
			currentParentEmail?.trim().toLowerCase() === normalizedParentEmail
		) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Please enter a different parent or guardian email address',
			});
			return null;
		}

		return trimmedParentEmail;
	};

	const submitAuthenticatedConsent = async (parentEmail: string) => {
		if (!isRegisteredUser) return false;

		setIsSignUpLoading(true);
		setIsParentConsentSubmitting(true);

		try {
			const consentStatus = isChangingParentEmail
				? await changeParentEmail({ parentEmail }).unwrap()
				: await requestConsent({ parentEmail }).unwrap();

			navigate(ROUTES.PARENT_EMAIL_VERIFICATION, {
				state: { email: consentStatus.parentEmail || parentEmail },
			});
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: getApiErrorMessage(error) || 'Something went wrong. Please try again.',
			});
		} finally {
			setIsSignUpLoading(false);
			setIsParentConsentSubmitting(false);
		}

		return true;
	};

	const requestInitialConsent = (parentEmail: string) => requestConsent({ parentEmail }).unwrap();

	return { validateParentEmail, submitAuthenticatedConsent, requestInitialConsent };
}
